import React, { useEffect } from "react";
import { Link } from "react-router-dom";

const PrivacyPolicy = () => {
  useEffect(() => {
    document.title = "Privacy Policy - Hyperapt";
    const m = document.querySelector('meta[name="description"]');
    if (m) m.setAttribute("content", "Privacy Policy for Hyperapt");
    else {
      const meta = document.createElement("meta");
      meta.name = "description";
      meta.content = "Privacy Policy for Hyperapt";
      document.head.appendChild(meta);
    }
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-web-bg text-web-text font-web-sans flex flex-col selection:bg-web-primary selection:text-white">

      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-white/90 backdrop-blur-md transition-all duration-300 h-20 flex items-center border-b border-web-border/50">
        <div className="max-w-7xl mx-auto px-4 w-full flex items-center justify-center">
          <Link to="/home" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <img src="/logo.png" alt="hyperapt" className="h-8 w-auto" />
            <span className="font-web-serif font-semibold text-[36px] tracking-normal text-web-text">
              hyperapt
            </span>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 w-full max-w-4xl mx-auto px-4 pt-32 pb-24">

        <div className="text-center mb-12">
          <h1 className="font-web-serif text-4xl lg:text-5xl font-bold text-web-text mb-4">Privacy Policy</h1>
          <p className="text-web-text/60 text-lg font-light">Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</p>
        </div>

        <div className="bg-white p-8 md:p-12 rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-web-border/60">

          <section className="mb-12">
            <h2 className="font-web-serif text-2xl font-bold mb-6 text-web-text">1. Introduction</h2>
            <div className="space-y-4 text-web-text/80 leading-relaxed font-light">
              <p>
                HYPERAPT INDIA PRIVATE LIMITED ("Hyperapt", "we", "our", or "us") respects your privacy and is committed to protecting your personal data. This Privacy Policy explains how we collect, use, process, and disclose your information when you use our digital prescription and receipt generation platform.
              </p>
              <p>
                By accessing or using our platform, you consent to the collection and use of your information as outlined in this policy.
              </p>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="font-web-serif text-2xl font-bold mb-6 text-web-text">2. Information We Collect</h2>
            <div className="space-y-4 text-web-text/80 leading-relaxed font-light">
              <p>We may collect the following types of information:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li><strong>Personal Identification Information:</strong> Name, age, gender, contact number, and address.</li>
                <li><strong>Health Information:</strong> Digital prescriptions generated by your consulting doctor, medical history provided during consultation, and dosage instructions.</li>
                <li><strong>Transaction Data:</strong> Details of orders placed and digital receipts generated.</li>
                <li><strong>Technical Data:</strong> IP address, browser type, device information, and platform usage metrics.</li>
              </ul>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="font-web-serif text-2xl font-bold mb-6 text-web-text">3. How We Use Your Information</h2>
            <div className="space-y-4 text-web-text/80 leading-relaxed font-light">
              <p>Your information is used strictly to provide and improve our services. Specifically, we use your data to:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Generate accurate digital prescriptions and GST-compliant receipts.</li>
                <li>Forward your prescriptions to authorized third-party pharmacies for order fulfillment (only with your explicit consent).</li>
                <li>Communicate order updates, tracking links, and digital receipts to your registered WhatsApp number or email.</li>
                <li>Maintain a secure digital health profile for you and your family members for seamless follow-ups.</li>
                <li>Comply with applicable legal and regulatory requirements.</li>
              </ul>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="font-web-serif text-2xl font-bold mb-6 text-web-text">4. Information Sharing & Third-Party Pharmacies</h2>
            <div className="space-y-4 text-web-text/80 leading-relaxed font-light">
              <p>
                Hyperapt operates purely as a technology platform. We do not store, manage, or sell any pharmacy inventory, medicines, or drugs.
              </p>
              <p>
                Upon your explicit consent during or after your consultation, we forward your digital prescription to our network of licensed third-party pharmacies. These third-party pharmacies are solely responsible for:
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Storing and managing the inventory of medicines.</li>
                <li>Verifying the prescription and fulfilling the order.</li>
                <li>Generating the final invoice (the invoice is the property and responsibility of the third-party pharmacy, not Hyperapt).</li>
              </ul>
              <p>
                We only share the necessary prescription and delivery details with these pharmacies to ensure your medicines are delivered swiftly. We do not sell your personal data to advertisers or unrelated third parties.
              </p>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="font-web-serif text-2xl font-bold mb-6 text-web-text">5. Data Security</h2>
            <div className="space-y-4 text-web-text/80 leading-relaxed font-light">
              <p>
                We employ industry-standard encryption, secure servers, and robust access controls to protect your personal and health information against unauthorized access, alteration, disclosure, or destruction. While we strive to use commercially acceptable means to protect your personal data, no method of transmission over the internet is 100% secure.
              </p>
            </div>
          </section>

          <section>
            <h2 className="font-web-serif text-2xl font-bold mb-6 text-web-text">6. Contact Us</h2>
            <div className="space-y-4 text-web-text/80 leading-relaxed font-light">
              <p>
                If you have any questions, concerns, or requests regarding this Privacy Policy or your personal data, please contact our Grievance Officer:
              </p>
              <p>
                <strong>Email:</strong> care@hyperapt.com<br />
                <strong>Phone:</strong> +91-9004669828
              </p>
            </div>
          </section>

        </div>
      </main>

      {/* Footer */}
      <footer className="bg-web-bg text-web-text py-8 border-t border-web-border mt-auto relative z-30">
        <div className="max-w-7xl mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-6">
          <Link to="/home" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <img src="/logo.png" alt="hyperapt" className="h-8 w-auto opacity-90" />
            <span className="font-web-serif font-semibold text-[32px] tracking-normal text-web-text">
              hyperapt
            </span>
          </Link>

          <nav className="flex flex-wrap justify-center gap-6 text-sm text-web-text/70 font-light">
            <Link to="/trackorder" className="hover:text-web-primary transition-colors">Track Order</Link>
            <Link to="/partner" className="hover:text-web-primary transition-colors">For Doctors</Link>
            <Link to="/terms" className="hover:text-web-primary transition-colors">Terms of Service</Link>
            <Link to="/privacypolicy" className="hover:text-web-primary transition-colors">Privacy Policy</Link>
            <Link to="/returnpolicy" className="hover:text-web-primary transition-colors">Return Policy</Link>
          </nav>

          <div className="text-sm text-web-text/50 font-light">
            © {new Date().getFullYear()} Hyperapt. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
